import { Link } from 'react-router-dom';
import Layout from '../../layout/Layout';
import { useI18n } from '../../contexts/I18nContext';

export default function Privacy() {
  const { legal, isRTL } = useI18n();
  const common = legal.legal.common;
  const sections = legal.legal.privacy.sections;

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-white via-[#F8F5FF] to-white py-16">
        <div className="container mx-auto px-4 sm:px-6 md:px-8 max-w-4xl">
          {/* Back Link */}
          <div className="pt-16 mb-8">
            <Link
              to="/legal"
              className={`inline-flex items-center text-[#5B3FD0] hover:text-[#4A2FB0] transition-colors font-medium ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <svg className={`w-5 h-5 ${isRTL ? 'ml-2 rotate-180' : 'mr-2'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              {legal.legal.pages.privacy.title}
            </Link>
          </div>

          {/* Header */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-8 border-t-4 border-[#5B3FD0]">
            <h1 className="text-3xl md:text-4xl font-bold text-[#2C2C2C] mb-4">
              {legal.legal.pages.privacy.title}
            </h1>
            <p className="text-[#707070] mb-2">{common.lastUpdated} February 12, 2026</p>
            <p className="text-[#707070] mb-2">{common.effectiveDate} February 12, 2026</p>
            <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#707070] mt-4">
              <span>{common.appName} <strong>Edu Nasser</strong></span>
              <span>{common.developer} <strong>ByteLogs LTD</strong></span>
              <span>{common.contact} <strong>support@edunasser.com</strong></span>
              <span>{common.website} <a href="https://edunasser.com" className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">https://edunasser.com</a></span>
            </div>
          </div>

          {/* Introduction */}
          <Section title={sections.introduction.title}>
            {sections.introduction.content.map((paragraph, i) => (
              <p key={i} className="text-[#555] leading-relaxed mb-3">{paragraph}</p>
            ))}
          </Section>

          {/* Information We Collect */}
          <Section title={sections.informationCollect.title}>
            {/* You Provide */}
            <SubSection title={sections.informationCollect.youProvide.title}>
              <ul className="space-y-2">
                {sections.informationCollect.youProvide.items.map((item, i) => (
                  <li key={i} className="text-[#555] leading-relaxed">
                    <strong className="text-[#2C2C2C]">{item.label}</strong> {item.text}
                  </li>
                ))}
              </ul>
            </SubSection>

            {/* Files and Media */}
            <SubSection title={sections.informationCollect.filesMedia.title}>
              <ul className="space-y-2">
                {sections.informationCollect.filesMedia.items.map((item, i) => (
                  <li key={i} className="text-[#555] leading-relaxed">
                    <strong className="text-[#2C2C2C]">{item.label}</strong> {item.text}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-[#707070] mt-3 italic">{sections.informationCollect.filesMedia.note}</p>
            </SubSection>

            {/* Automatic */}
            <SubSection title={sections.informationCollect.automatic.title}>
              <ul className="space-y-2">
                {sections.informationCollect.automatic.items.map((item, i) => (
                  <li key={i} className="text-[#555] leading-relaxed">
                    <strong className="text-[#2C2C2C]">{item.label}</strong> {item.text}
                  </li>
                ))}
              </ul>
            </SubSection>

            {/* Third Party */}
            <SubSection title={sections.informationCollect.thirdParty.title}>
              <ul className="space-y-2">
                {sections.informationCollect.thirdParty.items.map((item, i) => (
                  <li key={i} className="text-[#555] leading-relaxed">
                    <strong className="text-[#2C2C2C]">{item.label}</strong> {item.text}{' '}
                    <a href={item.url} className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">{item.link}</a>
                  </li>
                ))}
              </ul>
            </SubSection>
          </Section>

          {/* Permissions */}
          <Section title={sections.permissions.title}>
            <div className="space-y-6">
              {sections.permissions.items.map((perm, i) => (
                <div key={i} className="bg-[#F8F5FF] rounded-lg p-4">
                  <h4 className="font-bold text-[#2C2C2C] mb-1">{perm.title}</h4>
                  <p className="text-sm text-[#5B3FD0] font-medium mb-1">{perm.usedFor}</p>
                  <p className="text-[#555] leading-relaxed">{perm.description}</p>
                  {'list' in perm && (perm as any).list && (
                    <ul className="mt-2 space-y-1 list-disc list-inside text-[#555]">
                      {((perm as any).list as string[]).map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {'note' in perm && (perm as any).note && (
                    <p className="text-sm text-[#707070] mt-2 italic">{(perm as any).note}</p>
                  )}
                </div>
              ))}
            </div>
          </Section>

          {/* How We Use */}
          <Section title={sections.howWeUse.title}>
            <p className="text-[#555] mb-3">{sections.howWeUse.description}</p>
            <ul className="space-y-2 list-disc list-inside text-[#555]">
              {sections.howWeUse.items.map((item, i) => (
                <li key={i} className="leading-relaxed">{item}</li>
              ))}
            </ul>
          </Section>

          {/* Data Security */}
          <Section title={sections.dataSecurity.title}>
            <ul className="space-y-3">
              {sections.dataSecurity.items.map((item, i) => (
                <li key={i} className="text-[#555] leading-relaxed">
                  <strong className="text-[#2C2C2C]">{item.label}</strong> {item.text}
                </li>
              ))}
            </ul>
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 font-medium">{sections.dataSecurity.note} <strong>{sections.dataSecurity.noteStrong}</strong></p>
            </div>
          </Section>

          {/* Data Sharing */}
          <Section title={sections.dataSharing.title}>
            <p className="text-[#555] mb-3">{sections.dataSharing.description}</p>
            <ul className="space-y-3">
              {sections.dataSharing.items.map((item, i) => (
                <li key={i} className="text-[#555] leading-relaxed">
                  <strong className="text-[#2C2C2C]">{item.label}</strong> {item.text}
                  {'link' in item && (item as any).link && (
                    <>
                      {' '}
                      <a href={(item as any).url} className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">
                        {(item as any).link}
                      </a>
                    </>
                  )}
                </li>
              ))}
            </ul>
            <p className="text-sm text-[#707070] mt-4 italic">{sections.dataSharing.note}</p>
          </Section>

          {/* Your Rights */}
          <Section title={sections.yourRights.title}>
            <p className="text-[#555] mb-3">{sections.yourRights.description}</p>
            <ul className="space-y-3">
              {sections.yourRights.items.map((item, i) => (
                <li key={i} className="text-[#555] leading-relaxed">
                  <strong className="text-[#2C2C2C]">{item.label}</strong> {item.text}
                </li>
              ))}
            </ul>
            <p className="text-[#555] mt-4">
              {sections.yourRights.contactNote}{' '}
              <a href="mailto:support@edunasser.com" className="text-[#5B3FD0] hover:underline">support@edunasser.com</a>
            </p>
          </Section>

          {/* Data Retention */}
          <Section title={sections.dataRetention.title}>
            <ul className="space-y-2 list-disc list-inside text-[#555]">
              {sections.dataRetention.items.map((item, i) => (
                <li key={i} className="leading-relaxed">{item}</li>
              ))}
            </ul>
          </Section>

          {/* Children's Privacy */}
          <Section title={sections.children.title}>
            <p className="text-[#555] leading-relaxed">
              {sections.children.content}{' '}
              <a href="mailto:support@edunasser.com" className="text-[#5B3FD0] hover:underline">support@edunasser.com</a>
              {sections.children.note}
            </p>
          </Section>

          {/* International Data Transfers */}
          <Section title={sections.international.title}>
            <p className="text-[#555] leading-relaxed">{sections.international.content}</p>
          </Section>

          {/* Changes */}
          <Section title={sections.changes.title}>
            <p className="text-[#555] leading-relaxed">{sections.changes.content}</p>
          </Section>

          {/* Contact */}
          <Section title={sections.contact.title}>
            <p className="text-[#555] mb-4">{sections.contact.description}</p>
            <div className="bg-[#F8F5FF] rounded-lg p-6 space-y-2">
              <p className="text-[#555]">{common.company} <strong>ByteLogs LTD</strong></p>
              <p className="text-[#555]">{common.email} <a href="mailto:support@edunasser.com" className="text-[#5B3FD0] hover:underline">support@edunasser.com</a></p>
              <p className="text-[#555]">{common.website} <a href="https://edunasser.com" className="text-[#5B3FD0] hover:underline" target="_blank" rel="noopener noreferrer">https://edunasser.com</a></p>
            </div>
          </Section>

          {/* Jurisdiction */}
          <Section title={sections.jurisdiction.title}>
            <p className="text-[#555] leading-relaxed">{sections.jurisdiction.content}</p>
          </Section>

          {/* Data Safety Summary Table */}
          <Section title={sections.dataSafety.title}>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#5B3FD0] text-white">
                    <th className="px-4 py-3 text-left font-semibold">{sections.dataSafety.tableHeaders.dataType}</th>
                    <th className="px-4 py-3 text-left font-semibold">{sections.dataSafety.tableHeaders.collected}</th>
                    <th className="px-4 py-3 text-left font-semibold">{sections.dataSafety.tableHeaders.shared}</th>
                    <th className="px-4 py-3 text-left font-semibold">{sections.dataSafety.tableHeaders.purpose}</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.dataSafety.rows.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F8F5FF]'}>
                      <td className="px-4 py-3 text-[#2C2C2C] font-medium border-b">{row.dataType}</td>
                      <td className="px-4 py-3 text-[#555] border-b">{row.collected}</td>
                      <td className="px-4 py-3 text-[#555] border-b">{row.shared}</td>
                      <td className="px-4 py-3 text-[#555] border-b">{row.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-[#707070] mt-3 italic">{sections.dataSafety.note}</p>
          </Section>

          {/* Footer */}
          <div className="text-center mt-12">
            <p className="text-sm text-[#707070]">
              © {new Date().getFullYear()} {legal.legal.mainPage.footer.rights} <strong>{legal.legal.mainPage.company}</strong>
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
      <h2 className="text-2xl font-bold text-[#2C2C2C] mb-4 pb-2 border-b-2 border-[#5B3FD0]">{title}</h2>
      {children}
    </div>
  );
}

function SubSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="text-lg font-semibold text-[#2C2C2C] mb-3">{title}</h3>
      {children}
    </div>
  );
}
